import {
    Box, Button, TextField, Typography, Paper, Alert, Divider, Chip, CircularProgress
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import themeColors from "../theme";
import HeaderBar from "../components/HeaderBar";
import { useAuth } from "../context/AuthContext";
import { fetchSubscription, createPortalSession } from "../api/subscriptionApi";
import { formatPrice, statusChip, renewalSummary } from "../utils/subscription";

export default function AccountSettingsPage() {
    const { teal, darkTeal, lightGray } = themeColors;
    const { user, api } = useAuth();
    const navigate = useNavigate();

    // Email change state
    const [newEmail, setNewEmail] = useState("");
    const [emailPassword, setEmailPassword] = useState("");
    const [emailMsg, setEmailMsg] = useState(null); // { type, text }
    const [emailSubmitting, setEmailSubmitting] = useState(false);

    // Password change state
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordMsg, setPasswordMsg] = useState(null);
    const [passwordSubmitting, setPasswordSubmitting] = useState(false);

    // Subscription state
    const [billing, setBilling] = useState(null);
    const [billingLoading, setBillingLoading] = useState(true);
    const [billingErr, setBillingErr] = useState(null);
    const [portalLoading, setPortalLoading] = useState(false);

    const loadBilling = useCallback(async () => {
        try {
            setBilling(await fetchSubscription());
            setBillingErr(null);
        } catch {
            setBillingErr("Could not load your subscription details.");
        } finally {
            setBillingLoading(false);
        }
    }, []);

    useEffect(() => { loadBilling(); }, [loadBilling]);

    const handleManageBilling = async () => {
        setPortalLoading(true);
        setBillingErr(null);
        try {
            const { url } = await createPortalSession();
            window.location.href = url;
        } catch (err) {
            setBillingErr(err.response?.data?.error || "Could not open the billing portal. Please try again.");
            setPortalLoading(false);
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setEmailMsg(null);

        if (!newEmail || !emailPassword) {
            setEmailMsg({ type: "error", text: "Please fill in both fields." });
            return;
        }

        setEmailSubmitting(true);
        try {
            const res = await api.post("/api/change-email", {
                newEmail,
                currentPassword: emailPassword,
            });
            setEmailMsg({ type: "success", text: res.data.message });
            setNewEmail("");
            setEmailPassword("");
        } catch (err) {
            setEmailMsg({ type: "error", text: err.response?.data?.error || "Could not update email. Please try again." });
        } finally {
            setEmailSubmitting(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordMsg(null);

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordMsg({ type: "error", text: "Please fill in all fields." });
            return;
        }
        if (newPassword.length < 8) {
            setPasswordMsg({ type: "error", text: "New password must be at least 8 characters." });
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordMsg({ type: "error", text: "New passwords do not match." });
            return;
        }

        setPasswordSubmitting(true);
        try {
            const res = await api.post("/api/change-password", {
                currentPassword,
                newPassword,
            });
            setPasswordMsg({ type: "success", text: res.data.message });
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            setPasswordMsg({ type: "error", text: err.response?.data?.error || "Could not change password. Please try again." });
        } finally {
            setPasswordSubmitting(false);
        }
    };

    const sectionHeader = (Icon, title, subtitle) => (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3 }}>
            <Icon sx={{ color: teal, mt: 0.25 }} />
            <Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: "#1D3557", lineHeight: 1.2 }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="body2" sx={{ color: "#5A6C7D", mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    const submitButtonSx = {
        backgroundColor: teal,
        textTransform: "none",
        fontWeight: 600,
        px: 3,
        py: 1,
        "&:hover": { backgroundColor: darkTeal },
    };

    return (
        <>
            <HeaderBar />
            <Box sx={{ backgroundColor: lightGray, minHeight: "calc(100vh - 64px)", py: 5, px: 2 }}>
                <Box sx={{ maxWidth: 640, mx: "auto" }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: "#1D3557", mb: 1 }}>
                        Account Settings
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 4 }}>
                        Manage your login email, password, and subscription.
                    </Typography>

                    {/* Email */}
                    <Paper elevation={1} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
                        {sectionHeader(EmailOutlinedIcon, "Email Address", "This is the email you use to sign in.")}

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" sx={{ color: "#5A6C7D" }}>Current email</Typography>
                            <Typography variant="body1" fontWeight={500} sx={{ color: "#1D3557" }}>
                                {user?.email}
                            </Typography>
                            {user?.pendingEmail && (
                                <Chip
                                    size="small"
                                    label={`Pending verification: ${user.pendingEmail}`}
                                    sx={{ mt: 1, backgroundColor: "#fff3cd", color: "#856404" }}
                                />
                            )}
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {emailMsg && (
                            <Alert severity={emailMsg.type} sx={{ mb: 2 }} onClose={() => setEmailMsg(null)}>
                                {emailMsg.text}
                            </Alert>
                        )}

                        <Box component="form" onSubmit={handleEmailSubmit}>
                            <TextField
                                fullWidth
                                type="email"
                                label="New email address"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                sx={{ mb: 2 }}
                                autoComplete="email"
                            />
                            <TextField
                                fullWidth
                                type="password"
                                label="Current password"
                                value={emailPassword}
                                onChange={(e) => setEmailPassword(e.target.value)}
                                sx={{ mb: 1 }}
                                autoComplete="current-password"
                                helperText="For your security, confirm your password to change your email."
                            />
                            <Button type="submit" variant="contained" disabled={emailSubmitting} sx={submitButtonSx}>
                                {emailSubmitting ? "Sending..." : "Update Email"}
                            </Button>
                        </Box>
                    </Paper>

                    {/* Password */}
                    <Paper elevation={1} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
                        {sectionHeader(LockOutlinedIcon, "Password", "Choose a strong password you don't use elsewhere.")}

                        {passwordMsg && (
                            <Alert severity={passwordMsg.type} sx={{ mb: 2 }} onClose={() => setPasswordMsg(null)}>
                                {passwordMsg.text}
                            </Alert>
                        )}

                        <Box component="form" onSubmit={handlePasswordSubmit}>
                            <TextField
                                fullWidth
                                type="password"
                                label="Current password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                sx={{ mb: 2 }}
                                autoComplete="current-password"
                            />
                            <TextField
                                fullWidth
                                type="password"
                                label="New password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                sx={{ mb: 2 }}
                                autoComplete="new-password"
                                helperText="At least 8 characters."
                            />
                            <TextField
                                fullWidth
                                type="password"
                                label="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                sx={{ mb: 1 }}
                                autoComplete="new-password"
                            />
                            <Button type="submit" variant="contained" disabled={passwordSubmitting} sx={submitButtonSx}>
                                {passwordSubmitting ? "Saving..." : "Change Password"}
                            </Button>
                        </Box>
                    </Paper>

                    {/* Subscription */}
                    <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
                        {sectionHeader(CreditCardOutlinedIcon, "Subscription", "Manage your OnCallCME plan and billing.")}

                        {billingErr && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBillingErr(null)}>
                                {billingErr}
                            </Alert>
                        )}

                        {billingLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress size={24} sx={{ color: teal }} />
                            </Box>
                        ) : !billing?.billingEnabled ? (
                            <Alert severity="info">
                                Subscriptions aren't enabled yet. You'll be able to manage your plan here once billing
                                goes live.
                            </Alert>
                        ) : (
                            <>
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                                        <Typography variant="body1" fontWeight={600} sx={{ color: "#1D3557" }}>
                                            {billing.subscription.status ? billing.plan.name : "No active plan"}
                                        </Typography>
                                        <Chip size="small" {...statusChip(billing.subscription)} />
                                    </Box>

                                    {(() => {
                                        const priceLabel = formatPrice(billing.plan.priceCents, billing.plan.currency);
                                        const summary = renewalSummary(billing.subscription, priceLabel);
                                        return summary ? (
                                            <Typography variant="body2" sx={{ color: "#5A6C7D" }}>
                                                {summary}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: "#5A6C7D" }}>
                                                {priceLabel} per year. New subscribers can use FREEYEAR for their first year free.
                                            </Typography>
                                        );
                                    })()}

                                    {billing.subscription.promoCode && (
                                        <Chip
                                            size="small"
                                            label={`Code applied: ${billing.subscription.promoCode}`}
                                            sx={{ mt: 1.5, backgroundColor: "#E6F4F4", color: darkTeal, fontWeight: 600 }}
                                        />
                                    )}
                                </Box>

                                <Divider sx={{ mb: 3 }} />

                                {/* Users without a Stripe customer have never checked out, so
                                    the portal has nothing to show them — send them to subscribe. */}
                                {billing.subscription.hasCustomer ? (
                                    <Button
                                        variant="outlined"
                                        onClick={handleManageBilling}
                                        disabled={portalLoading}
                                        sx={{ textTransform: "none", fontWeight: 600 }}
                                    >
                                        {portalLoading ? "Opening..." : "Manage Subscription"}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate("/subscribe")}
                                        sx={{ ...submitButtonSx }}
                                    >
                                        View Plans
                                    </Button>
                                )}

                                {billing.subscription.hasCustomer && (
                                    <Typography variant="caption" sx={{ color: "#5A6C7D", display: "block", mt: 1.5 }}>
                                        Update your card, download invoices, or cancel in the Stripe billing portal.
                                    </Typography>
                                )}
                            </>
                        )}
                    </Paper>
                </Box>
            </Box>
        </>
    );
}
